'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useCallback, useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import {
  SALE_LABEL_FALLBACKS,
  SALE_MICROCOPY_MAP,
  getSaleController,
} from '../facetControllers';
import { StringFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { SaleFacetProps } from './SaleFacet.types';

const getValueId = (v: StringFlatFacetValue): string => v.value;

const SALE_SEED_VALUES: StringFlatFacetValue[] = [
  { value: 'flash_deal', state: 'idle', numberOfResults: 0 },
  { value: 'on_sale', state: 'idle', numberOfResults: 0 },
  { value: 'new_release_discount', state: 'idle', numberOfResults: 0 },
];

export const SaleFacet = ({
  variant = 'sidebar',
  className,
  isOpen,
  onToggle,
}: SaleFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const saleTitle =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.sale.title')?.value ??
    'Sale';

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getSaleController,
    fieldName: 'ec_sale_filters',
    getValueId,
    seedValues: SALE_SEED_VALUES,
  });

  const getLabel = useCallback(
    (value: string) => {
      const microcopyKey = SALE_MICROCOPY_MAP[value];
      if (microcopyKey) {
        const entry = searchLabelsMicrocopy?.find(
          (m) => m.key === microcopyKey
        );
        if (entry?.value) return entry.value;
      }
      return SALE_LABEL_FALLBACKS[value] ?? value;
    },
    [searchLabelsMicrocopy]
  );

  const treeItems = useMemo(
    () =>
      optimisticValues.map((v) => ({
        id: getValueId(v),
        label: getLabel(v.value),
        count: v.numberOfResults,
        disabled: v.numberOfResults === 0 && v.state !== 'selected',
      })),
    [optimisticValues, getLabel]
  );

  const hasVisibleValues = optimisticValues.some(
    (v) => v.numberOfResults > 0 || v.state === 'selected'
  );
  if (!hasVisibleValues) return null;

  const isDark = variant === 'mobile';

  const content = (
    <TreeAccordion
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={isDark}
      size={variant === 'sidebar' ? 'default' : 'sm'}
    />
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={saleTitle}
        selectedCount={selectedCount}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={saleTitle}
      variant={variant}
      className={className}
      onClear={handleClear}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
